export interface StyleOptions {
  /** URL стилей */
  href: string;

  /** Media query */
  media?: string;

  /** Для CORS запросов */
  crossOrigin?: 'anonymous' | 'use-credentials';

  /** Проверка целостности */
  integrity?: string;

  /** Политика referrer */
  referrerPolicy?: ReferrerPolicy;

  /** Не загружать повторно если уже есть */
  skipIfExists?: boolean;

  /** Отключить стиль после загрузки */
  disabled?: boolean;

  /** Заголовок для альтернативных стилей */
  title?: string;

  /** Произвольные data-атрибуты */
  dataset?: Record<string, string>;

  /** ID элемента */
  id?: string;

  /** Вставить перед определённым элементом */
  insertBefore?: HTMLElement | null;
}

export interface StyleLoadResult {
  element: HTMLLinkElement;
  cached: boolean;
}

export type StyleInput = StyleOptions | string;

/**
 * Загружает CSS стили динамически
 * 
 * @param optionsOrHref 
 * @returns 
 */
export function loadStyle(optionsOrHref: StyleInput): Promise<StyleLoadResult> {
  const options: StyleOptions = typeof optionsOrHref === 'string'
    ? { href: optionsOrHref }
    : optionsOrHref;

  const {
    href,
    media,
    crossOrigin,
    integrity,
    referrerPolicy,
    skipIfExists = true,
    disabled = false,
    title,
    dataset,
    id,
    insertBefore,
  } = options;

  return new Promise((resolve, reject) => {
    // Проверяем, не загружен ли уже стиль
    if (skipIfExists) {
      const existingLink = document.querySelector<HTMLLinkElement>(
        `link[href="${href}"]`
      );
      if (existingLink) {
        resolve({ element: existingLink, cached: true });
        return;
      }
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;

    if (media) link.media = media;
    if (crossOrigin) link.crossOrigin = crossOrigin;
    if (integrity) link.integrity = integrity;
    if (referrerPolicy) link.referrerPolicy = referrerPolicy;
    if (disabled) link.disabled = true;
    if (title) link.title = title;
    if (id) link.id = id;

    if (dataset) {
      Object.entries(dataset).forEach(([key, value]) => {
        link.dataset[key] = value;
      });
    }

    link.onload = () => resolve({ element: link, cached: false });
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));

    if (insertBefore) {
      document.head.insertBefore(link, insertBefore);
    } else {
      document.head.appendChild(link);
    }
  });
}

/**
 * Загружает инлайн CSS
 * 
 * @param css 
 * @param options 
 * @returns 
 */
export function loadInlineStyle(
  css: string,
  options?: { id?: string; media?: string }
): HTMLStyleElement {
  const style = document.createElement('style');

  if (options?.id) style.id = options.id;
  if (options?.media) style.media = options.media;

  style.textContent = css;
  document.head.appendChild(style);

  return style;
}

/**
 * Загружает несколько стилей последовательно
 * 
 * @param styles 
 * @returns 
 */
export async function loadStylesSequentially(
  styles: StyleInput[]
): Promise<StyleLoadResult[]> {
  const results: StyleLoadResult[] = [];

  for (const style of styles) {
    const result = await loadStyle(style);
    results.push(result);
  }

  return results;
}

/**
 * Загружает несколько стилей параллельно
 * 
 * @param styles 
 * @returns 
 */
export function loadStylesParallel(
  styles: StyleInput[]
): Promise<StyleLoadResult[]> {
  return Promise.all(styles.map((style) => loadStyle(style)));
}

/**
 * Переключает стиль (включает/выключает)
 * 
 * @param href 
 * @param enabled 
 * @returns 
 */
export function toggleStyle(href: string, enabled?: boolean): boolean {
  const link = document.querySelector<HTMLLinkElement>(`link[href="${href}"]`);
  if (link) {
    link.disabled = enabled !== undefined ? !enabled : !link.disabled;
    return true;
  }
  return false;
}

/**
 * Предзагрузка стилей (preload)
 * 
 * @param href 
 * @returns 
 */
export function preloadStyle(href: string): HTMLLinkElement {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'style';
  link.href = href;
  document.head.appendChild(link);
  return link;
}
